/**
 * Permissions Reference Page
 *
 * Read-only reference for all available permissions.
 * Edit role bundles on Roles; assign users/groups from Users or Groups;
 * add governance overrides on Policies.
 */

import { useState } from 'react';
import {
  Key,
  Search,
  Package,
  Layers,
  GitBranch,
  Lock,
  List,
  SlidersHorizontal,
  BarChart2,
  Tag,
  ArrowLeftRight,
  Network,
  Settings,
  Users,
  LayoutGrid,
  X,
} from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import {
  PERMISSION_DEFINITIONS,
  getPermissionsByCategory,
  type PermissionCategory,
} from '../../../types/prioritization/permissions';

// ============================================================================
// Constants
// ============================================================================

const PERMISSION_CATEGORIES: {
  id: PermissionCategory;
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
}[] = [
  { id: 'items',      label: 'Items',           description: 'Create, triage, view, edit, and delete prioritization items',              icon: Package },
  { id: 'process',    label: 'Cycles & Phases',  description: 'Manage cycles, advance phases, abort, and publish results',               icon: Layers },
  { id: 'tracks',     label: 'Tracks', description: 'Create and configure Tracks within cycles',                  icon: GitBranch },
  { id: 'session',    label: 'Sessions',         description: 'Facilitate, participate in, vote, and publish session results',            icon: Users },
  { id: 'pools',      label: 'Pool Governance',  description: 'Select, lock, and reopen item pools',                                     icon: Lock },
  { id: 'lists',      label: 'Item Pools',       description: 'Create and manage item pools and cut lines',                              icon: List },
  { id: 'criteria',   label: 'Criteria',         description: 'Define, select, weight, lock, and reopen criteria',                       icon: SlidersHorizontal },
  { id: 'values',     label: 'Values & Ranges',  description: 'Propose, accept, and override values; define and approve value ranges',   icon: BarChart2 },
  { id: 'attributes', label: 'Attributes',       description: 'Define attribute schemas and assign attribute owners',                    icon: Tag },
  { id: 'catchball',  label: 'Catchball',        description: 'Request, propose, return, accept, and reject catchball negotiations',     icon: ArrowLeftRight },
  { id: 'workflow',   label: 'Workflows',        description: 'Create, edit, archive, and assign workflows and templates',               icon: Network },
  { id: 'analytics',  label: 'Analytics',        description: 'View reports and export data',                                           icon: LayoutGrid },
  { id: 'admin',      label: 'Administration',   description: 'Manage users, groups, roles, policies, and system settings',             icon: Settings },
];

const TOTAL_PERMISSIONS = Object.keys(PERMISSION_DEFINITIONS).length;

// ============================================================================
// Component
// ============================================================================

export default function Permissions() {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<PermissionCategory | null>(null);

  const toggleCategory = (id: PermissionCategory) => {
    setActiveCategory(prev => prev === id ? null : id);
  };

  // All permissions filtered by search + active category
  const visiblePerms = Object.values(PERMISSION_DEFINITIONS).filter(p => {
    const matchesCategory = !activeCategory || p.category === activeCategory;
    if (!matchesCategory) return false;
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      p.label.toLowerCase().includes(q) ||
      p.description.toLowerCase().includes(q) ||
      p.id.toLowerCase().includes(q)
    );
  });

  // Count per category after search (for chip badges)
  const countForCategory = (id: PermissionCategory) => {
    if (!searchQuery) return getPermissionsByCategory(id).length;
    const q = searchQuery.toLowerCase();
    return getPermissionsByCategory(id).filter(p =>
      p.label.toLowerCase().includes(q) ||
      p.description.toLowerCase().includes(q) ||
      p.id.toLowerCase().includes(q)
    ).length;
  };

  const hasFilters = !!searchQuery || !!activeCategory;

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<Key className="h-4 w-4 text-primary-600" />}
        iconBg="bg-primary-50"
        title="Permissions"
        subtitle="Reference what each permission allows. Edit role bundles on Roles, manage overrides on Policies, and assign users or groups from Users or Groups."
      />

      {/* Context cards */}
      <div className="grid gap-3 md:grid-cols-3">
        {[
          { title: 'Roles', body: 'Create roles and choose which permissions each role grants.' },
          { title: 'Assignments', body: 'Assign users and groups to roles at the appropriate scope in the org hierarchy.' },
          { title: 'Policies', body: 'Add scoped overrides and governance rules that sit above role-based access.' },
        ].map(card => (
          <div key={card.title} className="border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] p-4">
            <div className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)] mb-1">{card.title}</div>
            <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)] leading-relaxed">{card.body}</p>
          </div>
        ))}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--text-ter)] dark:text-[var(--text-dim)]" />
        <input
          type="text"
          placeholder="Search permissions by name, description, or ID…"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-10 py-2 border border-[var(--rule)] dark:border-[var(--rule-bright)] text-sm bg-[var(--bg-raised)] dark:bg-[var(--surface)] text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder-gray-400 dark:placeholder-[var(--text-dim)] focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-ter)] dark:text-[var(--text-dim)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-sec)]"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Category filter chips */}
      <div className="flex flex-wrap gap-2">
        {PERMISSION_CATEGORIES.map(cat => {
          const count = countForCategory(cat.id);
          const isActive = activeCategory === cat.id;
          const Icon = cat.icon;
          if (searchQuery && count === 0) return null;

          return (
            <button
              key={cat.id}
              onClick={() => toggleCategory(cat.id)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-primary-600 border-primary-600 text-white'
                  : 'bg-[var(--bg-raised)] dark:bg-[var(--surface-raised)] border-[var(--rule)] dark:border-[var(--rule)] text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:border-[var(--rule)] dark:hover:border-[var(--rule-bright)] hover:text-[var(--text-pri)] dark:hover:text-[var(--text-pri)]'
              }`}
            >
              <Icon className="h-3.5 w-3.5 flex-shrink-0" />
              {cat.label}
              <span className={`tabular-nums text-xs ${isActive ? 'text-primary-200' : 'text-[var(--text-ter)] dark:text-[var(--text-dim)]'}`}>
                {count}
              </span>
            </button>
          );
        })}

        {hasFilters && (
          <button
            onClick={() => { setSearchQuery(''); setActiveCategory(null); }}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-dashed border-[var(--rule)] dark:border-[var(--rule)] text-sm text-[var(--text-ter)] dark:text-[var(--text-dim)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-sec)] hover:border-[var(--rule)] dark:hover:border-[var(--rule-bright)] transition-colors"
          >
            <X className="h-3.5 w-3.5" />
            Clear
          </button>
        )}
      </div>

      {/* Permission list */}
      <div className="bg-[var(--bg-raised)] dark:bg-[var(--surface)] border border-[var(--rule)] dark:border-[var(--rule)] overflow-hidden">
        {/* List header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--surface)]/60 dark:bg-[var(--surface-raised)]">
          <span className="text-xs font-semibold text-[var(--text-ter)] dark:text-[var(--text-dim)] uppercase tracking-wide">
            {activeCategory
              ? PERMISSION_CATEGORIES.find(c => c.id === activeCategory)?.label
              : 'All permissions'}
          </span>
          <span className="text-xs text-[var(--text-ter)] dark:text-[var(--text-dim)] tabular-nums">
            {visiblePerms.length} of {TOTAL_PERMISSIONS}
          </span>
        </div>

        {visiblePerms.length === 0 ? (
          <div className="px-5 py-16 text-center">
            <Search className="h-7 w-7 text-[var(--text-ter)] dark:text-[var(--text-dim)] mx-auto mb-3" />
            <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)]">
              No permissions match{searchQuery ? <> &ldquo;<strong>{searchQuery}</strong>&rdquo;</> : ' the selected filters'}.
            </p>
            <button
              onClick={() => { setSearchQuery(''); setActiveCategory(null); }}
              className="mt-2 text-sm text-primary-600 hover:underline"
            >
              Clear filters
            </button>
          </div>
        ) : (
          <div className="divide-y divide-[var(--rule)] dark:divide-[var(--rule)]">
            {visiblePerms.map(perm => {
              const catDef = PERMISSION_CATEGORIES.find(c => c.id === perm.category);
              const Icon = catDef?.icon;
              return (
                <div key={perm.id} className="flex items-start gap-4 px-5 py-3 hover:bg-[var(--surface)]/50 dark:hover:bg-[var(--hover-tint)] transition-colors">
                  {Icon && (
                    <div className="mt-0.5 flex-shrink-0 w-5 h-5 rounded flex items-center justify-center bg-[var(--hover-tint)] dark:bg-[var(--surface-raised)]">
                      <Icon className="h-3 w-3 text-[var(--text-ter)] dark:text-[var(--text-dim)]" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">{perm.label}</span>
                      <code className="text-xs text-[var(--text-ter)] dark:text-[var(--text-dim)] bg-[var(--hover-tint)] dark:bg-[var(--surface-raised)] px-1.5 py-0.5 rounded font-mono">{perm.id}</code>
                      {!activeCategory && catDef && (
                        <span className="text-xs text-[var(--text-ter)] dark:text-[var(--text-dim)]">{catDef.label}</span>
                      )}
                    </div>
                    <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)] mt-0.5 leading-relaxed">{perm.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
